<template>
  <div>
    <el-form ref="authForm" :model="authenticator.user" :rules="authRules">
      <el-form-item prop="username" class="mb-10">
        <el-input
          id="username"
          v-model="authenticator.user.username"
          :placeholder="$t('users.label.email')"
          @keyup.enter="handleAuthentication"
        />
      </el-form-item>
      <el-form-item prop="password" class="mb-10">
        <el-input
          :type="passwordInputType"
          class="show-password"
          v-model="authenticator.user.password"
          :placeholder="$t('users.label.password')"
          @:keyup.enter="handleAuthentication"
        >
          <img
            slot="suffix"
            :src="eyeSolid"
            v-if="passwordInputType === 'password'"
            class="el-input__icon password-icon-class"
            @click="changesPasswordInputType('text')"
          />
          <img
            slot="suffix"
            :src="eyeSlashSolid"
            v-else
            class="el-input__icon password-icon-class"
            @click="changesPasswordInputType('password')"
          />
        </el-input>
      </el-form-item>
      <div v-if="!isModal" class="text-left">
        <el-link
          class="forgot-password"
          @click="openResetPasswordModal()"
          :underline="false"
        >
          {{ $t("users.login.label.forgotPassword") }}
        </el-link>
        <el-checkbox v-model="rememberMe" class="remember-me">{{
          $t("users.login.rememberMe")
        }}</el-checkbox>
      </div>
      <div :class="[isModal ? 'text-left' : 'text-right mt-15']">
        <el-button
          type="primary"
          native-type="submit"
          class="ctaSubmit"
          @click="handleAuthentication"
        >
        {{ $t("shoppingCart.shoppingCartAuthModel.continue") }}
        </el-button>
      </div>
    </el-form>
    <el-dialog
      class="forgot-password-modal"
      :visible.sync="resetPasswordForm"
      :header="false"
      width="30%"
    >
      <h3 class="forget-passowrd-title text-left text-uppercase mb-5">
        {{ $t("users.forgotPasswordModal.title1") }}
      </h3>
      <span>{{ $t("users.forgotPasswordModal.text") }}</span>
      <el-form :model="resetPassword" ref="passReset" :rules="resetPassRules">
        <el-form-item prop="email" class="mb-5">
          <el-input
            id="email"
            :type="'email'"
            v-model="resetPassword.email"
            @:keyup.enter="handlePassReset"
            :placeholder="$t('users.forgotPasswordModal.placeHolder')"
          />
        </el-form-item>
        <el-button
          type="primary"
          native-type="submit"
          class="forgot-submit mt-15"
          @click="handlePassReset"
        >
          {{ $t("users.forgotPasswordModal.button") }}
        </el-button>
        <el-button @click="resetPasswordForm = false">{{
          $t("users.forgotPasswordModal.cancel")
        }}</el-button>
      </el-form>
    </el-dialog>
    <el-dialog
      class="forgot-password-modal"
      :visible.sync="resetPasswordSuccess"
      :header="false"
      width="24%"
    >
      <h3 class="forget-passowrd-title text-left text-uppercase mb-5">
        <i class="el-icon-success mr-5 success-icon"></i
        >{{ $t("users.forgotPasswordModal.title2") }}
      </h3>
      <span class="success-content"
        >{{ $t("users.forgotPasswordModal.successTextPart1") }}
        <b>{{ resetPassword.email }}</b>
        {{ $t("users.forgotPasswordModal.successTextPart2") }}</span
      >
      <el-button
        type="primary"
        native-type="submit"
        class="successs-btn mt-20 ph-8 pt-5 pr-5"
        @click="resetPasswordSuccessHandle"
      >
        {{ $t("users.forgotPasswordModal.successBtn") }}
      </el-button>
    </el-dialog>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { map } from "@/utils/lodash";
import Store from "@/store";
import eyeSolid from "@/assets/images/icons/password/eye-solid.svg";
import eyeSlashSolid from "@/assets/images/icons/password/eye-slash-solid.svg";
import goTo from "../../../mixins/goTo";
import userApi from "@/api/user";

export default {
  mixins: [goTo],
  props: {
    redirectPath: {
      type: String,
      default: "/account",
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    ctaSubmitText: {
      type: String,
      default: null,
    },
    toggleResetFields: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      authenticator: {
        user: {
          username: null,
          password: null,
        },
      },
      authRules: {
        username: [
          {
            required: true,
            message: this.$i18n.t("checkout.from.email"),
            trigger: "blur",
          },
        ],
        password: [
          {
            required: true,
            message: this.$i18n.t("checkout.from.password"),
            trigger: "blur",
          },
        ],
      },
      resetPassword: {
        email: null,
      },
      resetPassRules: {
        email: [
          {
            required: true,
            message: "Votre email est obligatoire",
            trigger: "blur",
          },
        ],
      },
      passwordInputType: "password",
      eyeSolid: eyeSolid,
      eyeSlashSolid: eyeSlashSolid,
      resetPasswordForm: false,
      resetPasswordSuccess: false,
      rememberMe: false,
    };
  },
  computed: {
    ...mapState("shoppingCart", ["shoppingCart"]),
    ...mapState("user", ["user"]),
    ...mapGetters("user", ["isLoggedIn"]),
    ...mapState("product", ["products"]),
    ...mapState("configuration", ["actionBar"]),
  },
  watch: {
    toggleResetFields() {
      this.resetFields();
    },
  },
  methods: {
    ...mapActions("user", ["authenticate"]),
    ...mapActions("shoppingCart", ["updateShoppingCart", "fetchShoppingCart"]),
    ...mapActions("product", ["fetchProductsByPuid"]),
    resetFields() {
      this.$refs.authForm.resetFields();
      this.$refs.passReset.resetFields();
    },
    handleAuthentication(e) {
      e.preventDefault();
      const vm = this;
      this.$refs.authForm.validate().then((valid) => {
        if (valid) {
          vm.authenticate({
            username: vm.authenticator.user.username,
            password: vm.authenticator.user.password,
          })
            .then(() => {
              if (vm.isLoggedIn) {
                const countryId = this.user?.portausCompany?.id;
                localStorage.setItem('actualCountryCode', countryId == 1 ? 'QC' : countryId == 2 ? 'ON' : 'QC' )
                localStorage.setItem('countryCode', countryId == 1 ? 'QC' : countryId == 2 ? 'ON' : 'QC' )
                const getCountryCode = localStorage.getItem("actualCountryCode");
                    if (this.actionBar.country !== getCountryCode) {
                      this.$store.dispatch('configuration/setActionBarCountry', { country: { code: getCountryCode } });
                    }
                vm.updateShoppingCartUser().then(() => {
                  if(this.user && this.user.passwordExpired == true){
                    vm.$router.push('/reset-password');
                  }
                  else if (vm.$route.query.redirect) {
                    vm.$router.push(vm.$route.query.redirect);
                  } else {
                    // The guest cart is stored per market as
                    // `cartProducts<countryCode>`; the unsuffixed key is never
                    // written, so this always read null and asked the catalogue
                    // for an empty puid list.
                    const cartKey = 'cartProducts' + localStorage.getItem('countryCode');
                    const getProducts = JSON.parse(localStorage.getItem(cartKey))
                    const puids = map(getProducts, (p) => p.puid).filter(Boolean)
                    if (puids.length) {
                      this.fetchProductsByPuid({
                        puids,
                      })
                    }
                    vm.$router.push('/account');
                  }
                });
                userApi.findById(JSON.parse(localStorage.user).user.id).then((user) => {
                  Store.dispatch("user/setUserProfile", user);
                });
              } else {
                vm.$emit("error", "cannot authenticate user");
              }
            })
            .catch((error) => {
              vm.$emit("error", error);
            });
          // remember me functionality
          if (vm.rememberMe) {
            const expirationDate = new Date();
            expirationDate.setDate(expirationDate.getDate() + 30);
            localStorage.setItem(
              "rememberedUser",
              JSON.stringify({
                username: vm.authenticator.user.username,
                password: vm.authenticator.user.password,
                expiryDate: expirationDate.toISOString(),
              })
            );
          } else {
            localStorage.removeItem("rememberedUser");
          }
        }
      });
    },
    updateShoppingCartUser() {
      const vm = this;
      // const shoppingCartId =
      //   get(this.shoppingCart, "id") || localStorage.getItem("x-cart");
      const user = {
        id: this.user.id,
      };
      const getId = JSON.parse(localStorage.getItem("user"));
      if (getId) {
        return Promise.resolve(
          this.fetchShoppingCart({
            id: getId.id,
          })
            .then(() => {
              const shoppingCart = {
                lockVersion: vm.shoppingCart.lockVersion,
                user,
                products: vm.shoppingCart.products,
              };
              const cartId = user.id
              vm.updateShoppingCart({
                id: cartId,
                shoppingCart,
              });
            })
            .catch((error) => {
              console.log(error);
            })
        );
      }
      return Promise.resolve();
    },

    changesPasswordInputType(type) {
      this.passwordInputType = type;
    },
    handlePassReset(e) {
      e.preventDefault();
      const vm = this;
      this.$refs.passReset.validate().then((valid) => {
        if (valid) {
          userApi
            .resetUserPassword({
              email: vm.resetPassword.email,
            })
            .then(() => {
              this.resetPasswordForm = false;
              this.resetPasswordSuccess = true;
            })
            .catch((error) => {
              vm.$emit("error", error);
            });
          }
      });
    },
    resetPasswordSuccessHandle(e) {
      e.preventDefault();
      this.resetPasswordSuccess = false;
      this.resetPassword.email = null;
    },
    openResetPasswordModal(){
      this.resetPassword.email = this.authenticator.user.username;
      this.resetPasswordForm = true;
    }
  },
  mounted() {
    const rememberedUser = localStorage.getItem("rememberedUser");
    if (rememberedUser) {
      const { username, password, expirationDate } = JSON.parse(rememberedUser);
      if (new Date(expirationDate) > new Date()) {
        this.authenticator.user.username = username;
        this.authenticator.user.password = password;
        this.rememberMe = true;
      } else {
        localStorage.removeItem("rememberedUser");
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.ctaSubmit {
  width: 150px;
  height: 40px;
}
.forgot-password {
  font-size: 16px;
  color: #7b020a;
}
.el-input__clear {
  color: #000 !important;
}
.password-icon-class {
  cursor: pointer;
  width: 20px;
  margin-right: 10px;
  pointer-events: unset;
}
.forgot-password-modal .el-dialog__header {
  padding: 0 !important;
}
.forgot-submit {
  height: auto;
  white-space: break-spaces;
}
.forgot-submit span {
  font-weight: 700;
  font-size: 15px;
}
.success-icon {
  color: green;
}
.successs-btn {
  padding: 0px 20px !important;
}
.successs-btn span {
  font-size: 15px;
}
.success-content {
  word-break: normal;
  display: block;
  margin-top: 20px;
}
.remember-me {
  float: right;
}
</style>
